import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNodeByKey, $getRoot } from 'lexical';
import { $createReportVisualNode } from '../nodes/ReportVisualNode';

export const REPORT_DRAFT_VISUAL_INSERT_MIME = 'application/x-report-draft-visual-insert';
export const VISUAL_DRAG_CLEAR_REASONS = {
  DROP: 'drop',
  CANCEL: 'cancel',
  LEAVE: 'leave',
  DISABLED: 'disabled',
  UNKNOWN: 'unknown',
};

const isFiniteNumber = (value) => Number.isFinite(Number(value));

export const normalizeVisualDragClearReason = (reason) => (
  Object.values(VISUAL_DRAG_CLEAR_REASONS).includes(reason)
    ? reason
    : VISUAL_DRAG_CLEAR_REASONS.UNKNOWN
);

export const getVisualDropZoneElement = (rootElement) => (
  rootElement?.parentElement || rootElement || null
);

export const parseVisualInsertPayload = (dataTransfer) => {
  if (!dataTransfer) {
    return null;
  }
  const candidates = [
    dataTransfer.getData(REPORT_DRAFT_VISUAL_INSERT_MIME),
    dataTransfer.getData('text/plain'),
  ].filter(Boolean);

  for (const raw of candidates) {
    try {
      const parsed = JSON.parse(raw);
      if (
        parsed
        && parsed.kind === 'visualInsertItem'
        && (parsed.visualType === 'chart' || parsed.visualType === 'map')
        && parsed.nodeData
        && typeof parsed.nodeData === 'object'
      ) {
        return parsed;
      }
    } catch (error) {
      // ignore and try fallback
    }
  }
  return null;
};

const buildTopLevelBlocks = (editor, rootElement) => {
  const blocks = [];
  editor.getEditorState().read(() => {
    const root = $getRoot();
    root.getChildren().forEach((node) => {
      const key = node.getKey();
      const element = editor.getElementByKey(key);
      if (!element || !rootElement.contains(element)) {
        return;
      }
      const rect = element.getBoundingClientRect();
      if (rect.height <= 0 || rect.width <= 0) {
        return;
      }
      blocks.push({
        key,
        rect: {
          top: rect.top,
          bottom: rect.bottom,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        },
      });
    });
  });
  return blocks;
};

export const computeVisualDropInsertTarget = ({ pointerY, rootRect, blocks }) => {
  const safeBlocks = Array.isArray(blocks) ? blocks : [];
  const fallbackLeft = (rootRect?.left || 0) + 8;
  const fallbackWidth = Math.max(40, (rootRect?.width || 0) - 16);

  if (!safeBlocks.length) {
    const top = isFiniteNumber(rootRect?.top) ? (rootRect.top + 18) : pointerY;
    return {
      target: { mode: 'append' },
      line: { top, left: fallbackLeft, width: fallbackWidth },
    };
  }

  for (const block of safeBlocks) {
    const midpoint = block.rect.top + (block.rect.height / 2);
    if (pointerY <= midpoint) {
      return {
        target: { mode: 'before', key: block.key },
        line: {
          top: block.rect.top,
          left: block.rect.left,
          width: block.rect.width,
        },
      };
    }
  }

  const last = safeBlocks[safeBlocks.length - 1];
  return {
    target: { mode: 'append' },
    line: {
      top: last.rect.bottom,
      left: last.rect.left,
      width: last.rect.width,
    },
  };
};

export const resolveVisualDropTargetFromPointer = ({
  pointerY,
  rootRect,
  blocks,
  fallbackTarget = null,
}) => {
  if (!isFiniteNumber(pointerY) || !rootRect) {
    return {
      target: fallbackTarget,
      line: null,
      usedFallback: true,
    };
  }
  const next = computeVisualDropInsertTarget({
    pointerY,
    rootRect,
    blocks,
  });
  if (!next?.target) {
    return {
      target: fallbackTarget,
      line: null,
      usedFallback: true,
    };
  }
  return {
    target: next.target,
    line: next.line || null,
    usedFallback: false,
  };
};

const cloneNodeData = (value) => {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return value;
  }
};

export function ReportDraftVisualDropInsertPlugin({
  enabled,
  onVisualDropCommit,
  onVisualDragClear,
}) {
  const [editor] = useLexicalComposerContext();
  const [line, setLine] = useState(null);
  const [isActiveDrag, setIsActiveDrag] = useState(false);
  const targetRef = useRef(null);
  const dragDepthRef = useRef(0);
  const leaveTimerRef = useRef(null);
  const isActiveDragRef = useRef(false);

  const clearDragState = useMemo(() => (reason = VISUAL_DRAG_CLEAR_REASONS.UNKNOWN, options = {}) => {
    const { notify = true } = options;
    const hadActiveDrag = Boolean(
      isActiveDragRef.current
      || targetRef.current
      || dragDepthRef.current > 0
    );
    targetRef.current = null;
    setLine(null);
    setIsActiveDrag(false);
    isActiveDragRef.current = false;
    dragDepthRef.current = 0;
    const rootElement = editor.getRootElement();
    getVisualDropZoneElement(rootElement)?.classList.remove('report-draft-editor__contenteditable-wrap--drag-over');
    if (leaveTimerRef.current) {
      window.clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    if (notify && hadActiveDrag && typeof onVisualDragClear === 'function') {
      onVisualDragClear(normalizeVisualDragClearReason(reason));
    }
  }, [editor, onVisualDragClear]);

  useEffect(() => {
    if (!enabled) {
      clearDragState(VISUAL_DRAG_CLEAR_REASONS.DISABLED, { notify: false });
      return undefined;
    }
    const rootElement = editor.getRootElement();
    if (!rootElement) {
      return undefined;
    }

    const rootWrap = getVisualDropZoneElement(rootElement);

    const updateFromPointer = (event) => {
      const payload = parseVisualInsertPayload(event.dataTransfer);
      if (!payload) {
        return false;
      }
      const rootRect = rootElement.getBoundingClientRect();
      const blocks = buildTopLevelBlocks(editor, rootElement);
      const next = computeVisualDropInsertTarget({
        pointerY: event.clientY,
        rootRect,
        blocks,
      });
      targetRef.current = next.target;
      setLine(next.line);
      setIsActiveDrag(true);
      isActiveDragRef.current = true;
      rootWrap?.classList.add('report-draft-editor__contenteditable-wrap--drag-over');
      return true;
    };

    const handleDragEnter = (event) => {
      dragDepthRef.current += 1;
      if (updateFromPointer(event)) {
        event.preventDefault();
      }
    };

    const handleDragOver = (event) => {
      if (!updateFromPointer(event)) {
        return;
      }
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
      if (leaveTimerRef.current) {
        window.clearTimeout(leaveTimerRef.current);
        leaveTimerRef.current = null;
      }
    };

    const handleDragLeave = () => {
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current > 0) {
        return;
      }
      if (leaveTimerRef.current) {
        window.clearTimeout(leaveTimerRef.current);
      }
      leaveTimerRef.current = window.setTimeout(() => {
        clearDragState(VISUAL_DRAG_CLEAR_REASONS.LEAVE);
      }, 40);
    };

    const handleDrop = (event) => {
      const payload = parseVisualInsertPayload(event.dataTransfer);
      if (!payload) {
        clearDragState(VISUAL_DRAG_CLEAR_REASONS.UNKNOWN);
        return;
      }
      event.preventDefault();
      const nextNodeData = cloneNodeData(payload.nodeData) || {};
      if (nextNodeData.source && typeof nextNodeData.source === 'object' && !nextNodeData.source.insertedAt) {
        nextNodeData.source = {
          ...nextNodeData.source,
          insertedAt: new Date().toISOString(),
        };
      }

      let target = targetRef.current;
      if (rootElement) {
        const finalTargetResult = resolveVisualDropTargetFromPointer({
          pointerY: event.clientY,
          rootRect: rootElement.getBoundingClientRect(),
          blocks: buildTopLevelBlocks(editor, rootElement),
          fallbackTarget: targetRef.current,
        });
        target = finalTargetResult.target || targetRef.current;
        if (finalTargetResult.line) {
          targetRef.current = finalTargetResult.target;
        }
      }
      editor.update(() => {
        const visualNode = $createReportVisualNode(nextNodeData);
        if (target?.mode === 'before' && target?.key) {
          const targetNode = $getNodeByKey(target.key);
          if (targetNode && typeof targetNode.insertBefore === 'function') {
            targetNode.insertBefore(visualNode);
            return;
          }
        }
        $getRoot().append(visualNode);
      });
      if (typeof onVisualDropCommit === 'function') {
        onVisualDropCommit(payload);
      }
      clearDragState(VISUAL_DRAG_CLEAR_REASONS.DROP);
    };

    const handleWindowDragEnd = () => {
      clearDragState(VISUAL_DRAG_CLEAR_REASONS.CANCEL);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isActiveDrag) {
        clearDragState(VISUAL_DRAG_CLEAR_REASONS.CANCEL);
      }
    };

    rootWrap?.addEventListener('dragenter', handleDragEnter);
    rootWrap?.addEventListener('dragover', handleDragOver);
    rootWrap?.addEventListener('dragleave', handleDragLeave);
    rootWrap?.addEventListener('drop', handleDrop);
    window.addEventListener('dragend', handleWindowDragEnd);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      rootWrap?.removeEventListener('dragenter', handleDragEnter);
      rootWrap?.removeEventListener('dragover', handleDragOver);
      rootWrap?.removeEventListener('dragleave', handleDragLeave);
      rootWrap?.removeEventListener('drop', handleDrop);
      window.removeEventListener('dragend', handleWindowDragEnd);
      window.removeEventListener('keydown', handleKeyDown);
      clearDragState(VISUAL_DRAG_CLEAR_REASONS.UNKNOWN, { notify: false });
    };
  }, [clearDragState, editor, enabled, isActiveDrag, onVisualDropCommit]);

  if (!enabled || !line || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      className="report-draft-editor__drop-insert-line"
      style={{
        top: `${line.top}px`,
        left: `${line.left}px`,
        width: `${line.width}px`,
      }}
      aria-hidden="true"
    >
      <span className="report-draft-editor__drop-insert-line-dot" />
    </div>,
    document.body
  );
}

ReportDraftVisualDropInsertPlugin.propTypes = {
  enabled: PropTypes.bool,
  onVisualDropCommit: PropTypes.func,
  onVisualDragClear: PropTypes.func,
};

ReportDraftVisualDropInsertPlugin.defaultProps = {
  enabled: true,
  onVisualDropCommit: undefined,
  onVisualDragClear: undefined,
};
